import { cardIdOrder, getCards, isWildcard, resolveRuleProfile, } from "./cards.js";
import { classifyCombination, combinationBeats } from "./combinations.js";
import { NATURAL_RANKS, SUITS, } from "./types.js";
function choose(values, size, limit = Number.POSITIVE_INFINITY) {
    if (size < 0 || size > values.length)
        return [];
    if (size === 0)
        return [[]];
    const result = [];
    const selection = [];
    const visit = (start) => {
        if (result.length >= limit)
            return;
        if (selection.length === size) {
            result.push([...selection]);
            return;
        }
        const remaining = size - selection.length;
        for (let index = start; index <= values.length - remaining; index += 1) {
            if (result.length >= limit)
                break;
            selection.push(values[index]);
            visit(index + 1);
            selection.pop();
        }
    };
    visit(0);
    return result;
}
function cartesian(parts) {
    let result = [[]];
    for (const part of parts) {
        result = result.flatMap((prefix) => part.map((suffix) => [...prefix, ...suffix]));
    }
    return result;
}
function allocations(length, maximumEach, maximumTotal) {
    const result = [];
    const current = [];
    const visit = (index, used) => {
        if (index === length) {
            result.push([...current]);
            return;
        }
        for (let count = 0; count <= maximumEach && used + count <= maximumTotal; count += 1) {
            current.push(count);
            visit(index + 1, used + count);
            current.pop();
        }
    };
    visit(0, 0);
    return result;
}
function naturalGroup(cards, rank, context) {
    return cards.filter((card) => card.rank === rank && !isWildcard(card, context.levelRank));
}
function sequenceWindows(length, allowWheel) {
    const windows = [];
    if (length === 5 && allowWheel)
        windows.push(["A", "2", "3", "4", "5"]);
    for (let index = 0; index <= NATURAL_RANKS.length - length; index += 1) {
        windows.push(NATURAL_RANKS.slice(index, index + length));
    }
    return windows;
}
function playOrder(left, right) {
    if (left.combination.isBomb !== right.combination.isBomb)
        return left.combination.isBomb ? 1 : -1;
    if (left.combination.cardCount !== right.combination.cardCount) {
        return right.combination.cardCount - left.combination.cardCount;
    }
    if (left.combination.mainValue !== right.combination.mainValue) {
        return left.combination.mainValue - right.combination.mainValue;
    }
    const kind = left.combination.kind.localeCompare(right.combination.kind);
    if (kind !== 0)
        return kind;
    return left.cardIds
        .join(",")
        .localeCompare(right.cardIds.join(","), undefined, { numeric: true });
}
export function enumerateLegalPlays(handLikes, incumbent, context, options = {}) {
    const hand = getCards(handLikes);
    const wildcards = hand.filter((card) => isWildcard(card, context.levelRank));
    const profile = resolveRuleProfile(context.ruleProfile);
    const selectionLimit = options.physicalVariants === false ? 1 : Number.POSITIVE_INFINITY;
    const candidates = new Map();
    const add = (cards) => {
        const key = cards
            .map((card) => card.id)
            .sort(cardIdOrder)
            .join(",");
        candidates.set(key, cards);
    };
    for (const card of hand)
        add([card]);
    const jokerPairs = ["SJ", "BJ"]
        .map((rank) => hand.filter((card) => card.rank === rank))
        .filter((cards) => cards.length === 2);
    for (const pair of jokerPairs)
        add(pair);
    for (const targetSize of [2, 3]) {
        for (const rank of NATURAL_RANKS) {
            const naturals = naturalGroup(hand, rank, context);
            for (let wildcardCount = 0; wildcardCount <= Math.min(wildcards.length, targetSize); wildcardCount += 1) {
                for (const naturalSelection of choose(naturals, targetSize - wildcardCount, selectionLimit)) {
                    for (const wildcardSelection of choose(wildcards, wildcardCount, selectionLimit))
                        add([...naturalSelection, ...wildcardSelection]);
                }
            }
        }
    }
    for (const tripleRank of NATURAL_RANKS) {
        for (const pairRank of NATURAL_RANKS) {
            if (tripleRank === pairRank)
                continue;
            const tripleNaturals = naturalGroup(hand, tripleRank, context);
            const pairNaturals = naturalGroup(hand, pairRank, context);
            for (let tripleWild = 0; tripleWild <= Math.min(3, wildcards.length); tripleWild += 1) {
                for (let pairWild = 0; pairWild <= Math.min(2, wildcards.length - tripleWild); pairWild += 1) {
                    const usedWild = tripleWild + pairWild;
                    const naturalSelections = cartesian([
                        choose(tripleNaturals, 3 - tripleWild, selectionLimit),
                        choose(pairNaturals, 2 - pairWild, selectionLimit),
                    ]);
                    for (const naturalSelection of naturalSelections) {
                        for (const wildcardSelection of choose(wildcards, usedWild, selectionLimit))
                            add([...naturalSelection, ...wildcardSelection]);
                    }
                }
            }
        }
    }
    for (const pair of jokerPairs) {
        for (const tripleRank of NATURAL_RANKS) {
            const tripleNaturals = naturalGroup(hand, tripleRank, context);
            for (let tripleWild = 0; tripleWild <= Math.min(3, wildcards.length); tripleWild += 1) {
                for (const naturalSelection of choose(tripleNaturals, 3 - tripleWild, selectionLimit)) {
                    for (const wildcardSelection of choose(wildcards, tripleWild, selectionLimit))
                        add([...naturalSelection, ...wildcardSelection, ...pair]);
                }
            }
        }
    }
    const addSequences = (length, multiplicity, suit) => {
        for (const ranks of sequenceWindows(length, profile.allowA2345Straight)) {
            for (const allocation of allocations(ranks.length, multiplicity, wildcards.length)) {
                const wildCount = allocation.reduce((sum, count) => sum + count, 0);
                const perRank = [];
                let valid = true;
                for (let index = 0; index < ranks.length; index += 1) {
                    const rank = ranks[index];
                    const allocatedWild = allocation[index];
                    const naturals = naturalGroup(hand, rank, context).filter((card) => suit === undefined || card.suit === suit);
                    const requiredNatural = multiplicity - allocatedWild;
                    const selections = choose(naturals, requiredNatural, selectionLimit);
                    if (selections.length === 0)
                        valid = false;
                    perRank.push(selections);
                }
                if (!valid)
                    continue;
                const eligibleWildcards = suit === undefined
                    ? wildcards
                    : profile.wildcardInStraightFlush
                        ? wildcards
                        : [];
                for (const naturalSelection of cartesian(perRank)) {
                    for (const wildcardSelection of choose(eligibleWildcards, wildCount, selectionLimit))
                        add([...naturalSelection, ...wildcardSelection]);
                }
            }
        }
    };
    addSequences(5, 1);
    addSequences(3, 2);
    addSequences(2, 3);
    for (const suit of SUITS)
        addSequences(5, 1, suit);
    for (const rank of NATURAL_RANKS) {
        const naturals = naturalGroup(hand, rank, context);
        for (let size = 4; size <= Math.min(10, naturals.length + wildcards.length); size += 1) {
            for (let wildcardCount = 0; wildcardCount <= Math.min(wildcards.length, size); wildcardCount += 1) {
                for (const naturalSelection of choose(naturals, size - wildcardCount, selectionLimit)) {
                    for (const wildcardSelection of choose(wildcards, wildcardCount, selectionLimit))
                        add([...naturalSelection, ...wildcardSelection]);
                }
            }
        }
    }
    const jokers = hand.filter((card) => card.rank === "SJ" || card.rank === "BJ");
    if (jokers.length === 4)
        add(jokers);
    const legal = [];
    for (const cards of candidates.values()) {
        const combination = classifyCombination(cards, context);
        if (combination && combinationBeats(combination, incumbent, context)) {
            legal.push(Object.freeze({ cardIds: combination.cardIds, combination }));
        }
    }
    return Object.freeze(legal.sort(playOrder));
}
export function isLegalPlay(hand, cardIds, incumbent, context) {
    const handSet = new Set(hand);
    if (cardIds.length === 0 ||
        new Set(cardIds).size !== cardIds.length ||
        cardIds.some((cardId) => !handSet.has(cardId))) {
        return false;
    }
    const combination = classifyCombination(cardIds, context);
    return (combination !== null && combinationBeats(combination, incumbent, context));
}
//# sourceMappingURL=legal.js.map